import React, { useState, useEffect, useMemo } from 'react';
import { DEFAULT_STUDENT_MAJORS } from '../constants/majors';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  IconButton,
  Tooltip,
  Divider,
  LinearProgress,
  InputAdornment,
  FormHelperText
} from '@mui/material';
import {
  MenuBook as ResearchIcon,
  Search as SearchIcon,
  CloudUpload as UploadIcon,
  Description as FileIcon,
  Article as ArticleIcon,
  Verified as CertIcon,
  Edit as EditIcon,
  CheckCircle as CheckIcon,
  Delete as DeleteIcon,
  FileDownload as DownloadIcon,
  School as SchoolIcon,
  LocalOffer as TagIcon,
  Close as CloseIcon,
  OpenInNew as OpenIcon,
  LibraryBooks as RepoIcon,
  AssignmentInd as ManageIcon,
  RestartAlt as RestartAltIcon,
  Schedule as ClockIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { GlassCard } from '../components/GlassCard';

const ClassroomResearchDatabase: React.FC = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isStudent = user.role === 'STUDENT';
  const isSupervisor = user.role === 'SUPERVISOR';
  const supervisorDept = (user.department || user.major || '').trim();
  // Co-Teachers can only search the repository; Management tab is for Supervisors, Staff, Admin, Dean, and Research Staff
  const canManageAll = ['SUPERVISOR', 'STAFF', 'ADMIN', 'DEAN', 'RESEARCH_STAFF'].includes(user.role);

  // Default tab: if student -> My Research (0) or Repository (1); if RESEARCH_STAFF -> Manage (1) or Repository (0)
  const [activeTab, setActiveTab] = useState<number>(0);

  const [loading, setLoading] = useState<boolean>(false);
  const [repositoryList, setRepositoryList] = useState<any[]>([]);
  const [myPlacements, setMyPlacements] = useState<any[]>([]);
  const [manageList, setManageList] = useState<any[]>([]);

  // Filters
  const [selectedYear, setSelectedYear] = useState<string>('ALL');
  const [selectedSemester, setSelectedSemester] = useState<string>('ALL');
  const [selectedMajor, setSelectedMajor] = useState<string>('ALL');
  const [selectedSchool, setSelectedSchool] = useState<string>('ALL');
  const [selectedStatus, setSelectedStatus] = useState<string>('ALL');
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Pagination
  const [repoPage, setRepoPage] = useState<number>(0);
  const [repoRowsPerPage, setRepoRowsPerPage] = useState<number>(15);
  const [managePage, setManagePage] = useState<number>(0);
  const [manageRowsPerPage, setManageRowsPerPage] = useState<number>(25);

  // Detail Modal (View Abstract & Files)
  const [detailDialogOpen, setDetailDialogOpen] = useState<boolean>(false);
  const [selectedDetail, setSelectedDetail] = useState<any>(null);

  // Edit / Upload Modal (For Student or Staff/Supervisor/Research Staff)
  const [editDialogOpen, setEditDialogOpen] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [editingRecord, setEditingRecord] = useState<any>(null);
  const [formState, setFormState] = useState({
    placement_id: '',
    title_th: '',
    title_en: '',
    abstract: '',
    keywords: '',
    advisor_name: '',
    co_teacher_name: '',
    target_grade_level: '',
    subject_name: '',
    utilization_org: '',
    status: 'SUBMITTED',
    reviewer_notes: '',
    report_5ch_url_external: '',
    article_url_external: '',
    utilization_cert_url_external: ''
  });
  const [report5chFile, setReport5chFile] = useState<File | null>(null);
  const [articleFile, setArticleFile] = useState<File | null>(null);
  const [certFile, setCertFile] = useState<File | null>(null);

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' | 'warning' }>({
    open: false,
    message: '',
    severity: 'success'
  });

  const resolveFileUrl = (url?: string) => {
    if (!url) return '';
    if (window.location.protocol === 'https:' && url.startsWith('http://')) {
      url = url.replace(/^http:\/\//i, 'https://');
    }
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    if (url.startsWith('/uploads/')) return `/api/v1${url}`;
    if (!url.startsWith('/')) return `/api/v1/uploads/${url}`;
    return url;
  };

  const fetchRepository = async () => {
    try {
      setLoading(true);
      const res = await api.get('/research/repository');
      setRepositoryList(res.data || []);
    } catch (err) {
      console.error('Failed to fetch research repository:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyResearch = async () => {
    if (!isStudent) return;
    try {
      setLoading(true);
      const res = await api.get('/research/my-research');
      setMyPlacements(res.data || []);
    } catch (err) {
      console.error('Failed to fetch my research:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchManageList = async () => {
    if (!canManageAll) return;
    try {
      setLoading(true);
      const res = await api.get('/research/manage-list');
      setManageList(res.data || []);
    } catch (err) {
      console.error('Failed to fetch manage research list:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRepository();
    if (isStudent) {
      fetchMyResearch();
    }
    if (canManageAll) {
      fetchManageList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Extract filter options
  const sourceForFilters = activeTab === 0 ? repositoryList : manageList;
  const availableYears = useMemo(() => {
    const yrs = new Set<string>(['2569', '2568', '2567']);
    sourceForFilters.forEach(r => { if (r.academic_year) yrs.add(String(r.academic_year)); });
    return Array.from(yrs).sort((a, b) => Number(b) - Number(a));
  }, [sourceForFilters]);

  const availableMajors = useMemo(() => {
    if (activeTab === 1 && isSupervisor && supervisorDept) {
      return [supervisorDept];
    }
    const DEFAULT_MAJORS = Array.from(new Set([
      ...DEFAULT_STUDENT_MAJORS,
      'คณิตศาสตร์ประยุกต์'
    ])).sort((a, b) => a.localeCompare(b, 'th'));
    const m = new Set<string>(DEFAULT_MAJORS);
    sourceForFilters.forEach(r => { if (r.major) m.add(r.major); });
    return Array.from(m).sort((a, b) => a.localeCompare(b, 'th'));
  }, [sourceForFilters, activeTab, isSupervisor, supervisorDept]);

  const availableSchools = useMemo(() => {
    const s = new Set<string>();
    sourceForFilters.forEach(r => { if (r.school_name) s.add(r.school_name); });
    return Array.from(s).sort();
  }, [sourceForFilters]);

  // Filtered repository list
  const filteredRepo = useMemo(() => {
    return repositoryList.filter(r => {
      const matchYear = selectedYear === 'ALL' || String(r.academic_year) === selectedYear;
      const matchSem = selectedSemester === 'ALL' || String(r.semester) === selectedSemester;
      const matchMajor = selectedMajor === 'ALL' || r.major === selectedMajor;
      const matchSchool = selectedSchool === 'ALL' || r.school_name === selectedSchool;
      const q = searchQuery.trim().toLowerCase();
      const matchSearch = !q ||
        (r.title_th || '').toLowerCase().includes(q) ||
        (r.title_en || '').toLowerCase().includes(q) ||
        (r.keywords || '').toLowerCase().includes(q) ||
        (r.abstract || '').toLowerCase().includes(q) ||
        `${r.prefix || ''}${r.first_name || ''} ${r.last_name || ''}`.toLowerCase().includes(q) ||
        (r.student_code || '').toLowerCase().includes(q) ||
        (r.school_name || '').toLowerCase().includes(q);
      return matchYear && matchSem && matchMajor && matchSchool && matchSearch;
    });
  }, [repositoryList, selectedYear, selectedSemester, selectedMajor, selectedSchool, searchQuery]);

  // Filtered manage list
  const filteredManage = useMemo(() => {
    return manageList.filter(r => {
      // If Supervisor, enforce isolation to matching department
      if (isSupervisor && supervisorDept) {
        const cleanSup = supervisorDept.includes('(')
          ? supervisorDept.split('(')[1].split(')')[0].trim()
          : supervisorDept.replace('วิทยาศาสตร์ศึกษา', '').replace('การศึกษา', '').replace('ศึกษา', '').trim();
        const stdMajor = (r.major || '').trim();
        if (stdMajor !== supervisorDept) {
          const cleanStd = stdMajor.includes('(')
            ? stdMajor.split('(')[1].split(')')[0].trim()
            : stdMajor.replace('วิทยาศาสตร์ศึกษา', '').replace('การศึกษา', '').replace('ศึกษา', '').trim();
          if (cleanSup !== cleanStd) return false;
        }
      }

      const matchYear = selectedYear === 'ALL' || String(r.academic_year) === selectedYear;
      const matchSem = selectedSemester === 'ALL' || String(r.semester) === selectedSemester;
      const matchMajor = selectedMajor === 'ALL' || r.major === selectedMajor;
      const matchSchool = selectedSchool === 'ALL' || r.school_name === selectedSchool;

      let matchStatus = true;
      if (selectedStatus === 'NOT_SUBMITTED') {
        matchStatus = !r.report_5ch_url && !r.article_url && !r.utilization_cert_url;
      } else if (selectedStatus === 'HAS_5CH') {
        matchStatus = Boolean(r.report_5ch_url);
      } else if (selectedStatus === 'HAS_ARTICLE') {
        matchStatus = Boolean(r.article_url);
      } else if (selectedStatus === 'HAS_CERT') {
        matchStatus = Boolean(r.utilization_cert_url);
      } else if (selectedStatus !== 'ALL') {
        matchStatus = r.research_status === selectedStatus;
      }

      const q = searchQuery.trim().toLowerCase();
      const matchSearch = !q ||
        (r.title_th || '').toLowerCase().includes(q) ||
        (r.title_en || '').toLowerCase().includes(q) ||
        (r.keywords || '').toLowerCase().includes(q) ||
        `${r.prefix || ''}${r.first_name || ''} ${r.last_name || ''}`.toLowerCase().includes(q) ||
        (r.student_code || '').toLowerCase().includes(q) ||
        (r.school_name || '').toLowerCase().includes(q);

      return matchYear && matchSem && matchMajor && matchSchool && matchStatus && matchSearch;
    });
  }, [manageList, selectedYear, selectedSemester, selectedMajor, selectedSchool, selectedStatus, searchQuery, isSupervisor, supervisorDept]);

  // Open Edit Modal
  const handleOpenEdit = (record: any) => {
    setEditingRecord(record);
    const hasWorkloadEval = Boolean(record.workload_evaluation_id);
    let initialStatus = record.research_status || record.status || 'SUBMITTED';
    if (!hasWorkloadEval && initialStatus === 'PUBLISHED') {
      initialStatus = 'SUBMITTED';
    }
    setFormState({
      placement_id: record.placement_id,
      title_th: record.title_th || '',
      title_en: record.title_en || '',
      abstract: record.abstract || '',
      keywords: record.keywords || '',
      advisor_name: record.advisor_name || (record.default_advisor_name ? record.default_advisor_name.trim() : ''),
      co_teacher_name: record.co_teacher_name || (record.default_coteacher_name ? record.default_coteacher_name.trim() : ''),
      target_grade_level: record.target_grade_level || '',
      subject_name: record.subject_name || '',
      utilization_org: record.utilization_org || record.school_name || '',
      status: initialStatus,
      reviewer_notes: record.reviewer_notes || '',
      report_5ch_url_external: '',
      article_url_external: '',
      utilization_cert_url_external: ''
    });
    setReport5chFile(null);
    setArticleFile(null);
    setCertFile(null);
    setEditDialogOpen(true);
  };

  const handleSaveResearch = async () => {
    if (!formState.title_th.trim()) {
      setSnackbar({ open: true, message: 'กรุณาระบุชื่อเรื่องงานวิจัยในชั้นเรียน (ภาษาไทย)', severity: 'error' });
      return;
    }

    if (formState.status === 'PUBLISHED' && editingRecord && !editingRecord.workload_evaluation_id) {
      setSnackbar({
        open: true,
        message: 'ไม่สามารถอนุมัติและเผยแพร่ได้ เนื่องจากนักศึกษายังไม่ได้รับการบันทึกคะแนนประเมินภาระงานจากอาจารย์นิเทศก์ กรุณาบันทึกคะแนนภาระงานก่อน',
        severity: 'warning'
      });
      return;
    }

    try {
      setSaving(true);
      const fd = new FormData();
      Object.entries(formState).forEach(([k, v]) => {
        if (v !== undefined && v !== null) {
          fd.append(k, String(v));
        }
      });
      if (report5chFile) fd.append('report_5ch_file', report5chFile);
      if (articleFile) fd.append('article_file', articleFile);
      if (certFile) fd.append('utilization_cert_file', certFile);

      await api.post('/research/save', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setSnackbar({ open: true, message: 'บันทึกข้อมูลผลงานวิจัยในชั้นเรียนเรียบร้อยแล้ว', severity: 'success' });
      setEditDialogOpen(false);
      fetchRepository();
      if (isStudent) fetchMyResearch();
      if (canManageAll) fetchManageList();
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'เกิดข้อผิดพลาดในการบันทึกข้อมูลงานวิจัย',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteFile = async (researchId: string, fileType: 'report_5ch' | 'article' | 'utilization_cert' | 'feedback', label: string) => {
    const targetId = researchId || editingRecord?.research_id || editingRecord?.placement_id;
    if (!targetId) return;
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    if (!window.confirm(`ยืนยันการลบไฟล์ "${label}" หรือไม่?\n\nเมื่อลบแล้วไฟล์จะถูกนำออกจากระบบทันที และท่านสามารถเลือกไฟล์ใหม่เพื่ออัปโหลดแทนที่ได้`)) return;
    try {
      await api.delete(`/research/${targetId}/file/${fileType}`);
      setSnackbar({ open: true, message: `ลบไฟล์ ${label} เรียบร้อยแล้ว`, severity: 'info' });
      fetchRepository();
      if (isStudent) fetchMyResearch();
      if (canManageAll) fetchManageList();

      // Keep dialog open and update editingRecord in-place
      if (editingRecord) {
        const updated = { ...editingRecord };
        if (fileType === 'report_5ch') {
          updated.report_5ch_url = null;
          updated.report_5ch_name = null;
          updated.workload_5ch_url = null;
          updated.workload_5ch_name = null;
          setReport5chFile(null);
        } else if (fileType === 'article') {
          updated.article_url = null;
          updated.article_name = null;
          setArticleFile(null);
        } else if (fileType === 'utilization_cert') {
          updated.utilization_cert_url = null;
          updated.utilization_cert_name = null;
          setCertFile(null);
        } else if (fileType === 'feedback') {
          updated.feedback_file_url = null;
          updated.feedback_file_name = null;
        }
        setEditingRecord(updated);
      }
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'ไม่สามารถลบไฟล์ได้', severity: 'error' });
    }
  };

  const handleResetResearch = async (record: any) => {
    if (!record) return;
    const targetId = record.placement_id || record.research_id || record.id;
    if (!targetId) return;

    const studentName = `${record.prefix || ''}${record.first_name || ''} ${record.last_name || ''}`.trim();
    const confirmMsg = `ยืนยันการรีเซตข้อมูลงานวิจัยของ "${studentName}" (${record.student_code || '-'}) หรือไม่?\n\nการดำเนินการนี้จะ:\n1. ลบไฟล์วิจัย 5 บท, บทความวิจัย และหนังสือรับรองฯ ทั้งหมด\n2. ลบไฟล์ที่ส่งในระบบภาระงานวิจัยของนักศึกษา\n3. รีเซตสถานะการส่งงานวิจัยเพื่อให้นักศึกษาสามารถอัปโหลดส่งใหม่ได้\n\nคำเตือน: ข้อมูลและไฟล์ที่ถูกลบจะไม่สามารถกู้คืนได้`;

    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    if (!window.confirm(confirmMsg)) return;

    try {
      setSaving(true);
      await api.post(`/research/reset/${targetId}`);
      setSnackbar({ open: true, message: `รีเซตข้อมูลและไฟล์งานวิจัยของ ${studentName} เรียบร้อยแล้ว`, severity: 'success' });
      fetchRepository();
      if (canManageAll) fetchManageList();
      if (isStudent) fetchMyResearch();
      setEditDialogOpen(false);
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'เกิดข้อผิดพลาดในการรีเซตข้อมูลงานวิจัย', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleExportExcel = () => {
    const source = activeTab === 0 ? filteredRepo : filteredManage;
    const rows = source.map((r, idx) => ({
      'ลำดับ': idx + 1,
      'รหัสนักศึกษา': r.student_code || '-',
      'ชื่อ-สกุลผู้วิจัย': `${r.prefix || ''}${r.first_name || ''} ${r.last_name || ''}`.trim(),
      'สาขาวิชา': r.major || '-',
      'โรงเรียนฝึกประสบการณ์': r.school_name || '-',
      'ปีการศึกษา/ภาคเรียน': `${r.semester || '-'}/${r.academic_year || '-'}`,
      'ชื่อเรื่องวิจัย (ภาษาไทย)': r.title_th || '(ยังไม่ได้ระบุ)',
      'ชื่อเรื่องวิจัย (ภาษาอังกฤษ)': r.title_en || '-',
      'คำสำคัญ (Keywords)': r.keywords || '-',
      'ระดับชั้นที่ทำวิจัย': r.target_grade_level || '-',
      'อาจารย์ที่ปรึกษา': r.advisor_name || '-',
      'รายงานวิจัยในชั้นเรียน': r.report_5ch_url ? 'ส่งแล้ว' : 'ยังไม่ส่ง',
      'บทความวิจัย': r.article_url ? 'ส่งแล้ว' : 'ยังไม่ส่ง',
      'หนังสือรับรองการนำไปใช้ประโยชน์': r.utilization_cert_url ? 'ส่งแล้ว' : 'ยังไม่ส่ง',
      'สถานะ': r.research_status || r.status || 'ยังไม่ส่ง'
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Classroom_Research_DB');
    XLSX.writeFile(wb, `ฐานข้อมูลงานวิจัยในชั้นเรียน_TPMS_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const getStatusChip = (status?: string) => {
    switch (status) {
      case 'PUBLISHED':
      case 'VERIFIED':
        return <Chip label="อนุมัติเผยแพร่ในฐานข้อมูล" color="success" size="small" sx={{ fontWeight: 700 }} />;
      case 'REVISION_REQUESTED':
        return <Chip label="ขอให้นักศึกษาปรับปรุงแก้ไข" color="warning" size="small" sx={{ fontWeight: 700 }} />;
      case 'SUBMITTED':
        return <Chip label="ส่งผลงานแล้ว (รอตรวจสอบ)" color="primary" variant="outlined" size="small" sx={{ fontWeight: 600 }} />;
      default:
        return <Chip label="ยังไม่ส่งข้อมูล" color="default" variant="outlined" size="small" />;
    }
  };

  // Statistics
  const totalResearches = repositoryList.length;
  const count5Ch = repositoryList.filter(r => r.report_5ch_url).length;
  const countArticle = repositoryList.filter(r => r.article_url).length;
  const countCert = repositoryList.filter(r => r.utilization_cert_url).length;

  return (
    <Box>
      {/* PTLE Standard Page Header & KPI Cards */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="overline" color="primary" sx={{ fontWeight: 700 }}>
            CLASSROOM ACTION RESEARCH DATABASE
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#0F172A' }}>
            ระบบฐานข้อมูลงานวิจัยในชั้นเรียน
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            คลังความรู้และฐานข้อมูลสืบค้นผลงานวิจัยในชั้นเรียน บทความวิจัย และหนังสือรับรองการนำไปใช้ประโยชน์ คณะครุศาสตร์ มหาวิทยาลัยราชภัฏกำแพงเพชร
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="success"
          startIcon={<DownloadIcon />}
          onClick={handleExportExcel}
          sx={{ borderRadius: 2.5, fontWeight: 600 }}
        >
          ส่งออกข้อมูลวิจัย (Excel)
        </Button>
      </Box>

      {/* Summary KPI Strip (PTLE GlassCard Grid) */}
      <Grid container spacing={2.5} sx={{ mb: 3.5 }}>
        <Grid size={{ xs: 6, sm: 3 }}>
          <GlassCard highlightColor="#1E40AF" sx={{ p: 2.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              ผลงานวิจัยในฐานข้อมูล
            </Typography>
            <Typography variant="h4" color="primary.main" sx={{ fontWeight: 700, mt: 0.5 }}>
              {totalResearches} <Typography component="span" variant="body2" color="text.secondary">เรื่อง</Typography>
            </Typography>
          </GlassCard>
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <GlassCard highlightColor="#0284C7" sx={{ p: 2.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              รายงานวิจัยในชั้นเรียน
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#0284C7', mt: 0.5 }}>
              {count5Ch} <Typography component="span" variant="body2" color="text.secondary">ฉบับ</Typography>
            </Typography>
          </GlassCard>
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <GlassCard highlightColor="#059669" sx={{ p: 2.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              บทความวิจัย (Research Article)
            </Typography>
            <Typography variant="h4" color="success.main" sx={{ fontWeight: 700, mt: 0.5 }}>
              {countArticle} <Typography component="span" variant="body2" color="text.secondary">ฉบับ</Typography>
            </Typography>
          </GlassCard>
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <GlassCard highlightColor="#D97706" sx={{ p: 2.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              หนังสือรับรองการใช้ประโยชน์
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#D97706', mt: 0.5 }}>
              {countCert} <Typography component="span" variant="body2" color="text.secondary">ฉบับ</Typography>
            </Typography>
          </GlassCard>
        </Grid>
      </Grid>

      {/* Student Guidance Alert */}
      {isStudent && (
        <Alert
          severity="info"
          icon={<UploadIcon />}
          sx={{ mb: 3, borderRadius: 2 }}
          action={
            <Button
              color="primary"
              size="small"
              variant="outlined"
              onClick={() => navigate('/dashboard/workload-submission')}
              sx={{ fontWeight: 700, borderRadius: 2 }}
            >
              ไปยังระบบส่งภาระงาน
            </Button>
          }
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            📌 ระบบฐานข้อมูลงานวิจัยในชั้นเรียนสำหรับนักศึกษา
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.3 }}>
            ท่านสามารถตรวจสอบสถานะ อัปโหลด หรือแก้ไขไฟล์ <strong>รายงานวิจัย 5 บท</strong>, <strong>บทความวิจัย (Research Article)</strong> และ <strong>หนังสือรับรองการนำไปใช้ประโยชน์</strong> ได้โดยตรงที่ตารางและแบบฟอร์มด้านล่างนี้ หรือผ่านระบบส่งภาระงานประจำวิชา ซึ่งข้อมูลจะเชื่อมโยงถึงกันโดยอัตโนมัติ
          </Typography>
        </Alert>
      )}

      {/* Navigation Tabs */}
      <Paper sx={{ mb: 3, borderRadius: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(_, val) => setActiveTab(val)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ px: 2, borderBottom: '1px solid #e2e8f0' }}
        >
          {isStudent ? [
            <Tab
              key="my-research"
              icon={<ResearchIcon />}
              iconPosition="start"
              label="งานวิจัยในชั้นเรียนของฉัน (ส่ง/แก้ไข บทความวิจัย และหนังสือรับรองฯ)"
              sx={{ fontWeight: 700, py: 2 }}
            />,
            <Tab
              key="repo"
              icon={<RepoIcon />}
              iconPosition="start"
              label="สืบค้นคลังงานวิจัยในชั้นเรียนทั้งหมด"
              sx={{ fontWeight: 700, py: 2 }}
            />
          ] : [
            <Tab
              key="repo"
              icon={<RepoIcon />}
              iconPosition="start"
              label="สืบค้นฐานข้อมูลงานวิจัยในชั้นเรียน (สำหรับนักศึกษาและบุคลากร)"
              sx={{ fontWeight: 700, py: 2 }}
            />,
            ...(canManageAll ? [
              <Tab
                key="manage"
                icon={<ManageIcon />}
                iconPosition="start"
                label="จัดการและตรวจสอบงานวิจัยของนักศึกษาทั้งหมด"
                sx={{ fontWeight: 700, py: 2 }}
              />
            ] : [])
          ]}
        </Tabs>
      </Paper>

      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

      {/* TAB: SEARCHABLE RESEARCH REPOSITORY (Tab 0 for non-students, Tab 1 for students) */}
      {((!isStudent && activeTab === 0) || (isStudent && activeTab === 1)) && (
        <Box>
          <Paper sx={{ p: 2.5, mb: 3, borderRadius: 2 }}>
            <Grid container spacing={2} sx={{ alignItems: 'center' }}>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="ค้นหาชื่อเรื่องงานวิจัย, คำสำคัญ (Keywords), บทคัดย่อ, ชื่อผู้วิจัย..."
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setRepoPage(0); }}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon color="primary" />
                        </InputAdornment>
                      )
                    }
                  }}
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>ปีการศึกษา</InputLabel>
                  <Select value={selectedYear} label="ปีการศึกษา" onChange={(e) => { setSelectedYear(e.target.value); setRepoPage(0); }}>
                    <MenuItem value="ALL">ทุกปีการศึกษา</MenuItem>
                    {availableYears.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 6, sm: 2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>ภาคเรียน</InputLabel>
                  <Select value={selectedSemester} label="ภาคเรียน" onChange={(e) => { setSelectedSemester(e.target.value); setRepoPage(0); }}>
                    <MenuItem value="ALL">ทุกภาคเรียน</MenuItem>
                    <MenuItem value="1">ภาคเรียนที่ 1</MenuItem>
                    <MenuItem value="2">ภาคเรียนที่ 2</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>สาขาวิชา</InputLabel>
                  <Select value={selectedMajor} label="สาขาวิชา" onChange={(e) => { setSelectedMajor(e.target.value); setRepoPage(0); }}>
                    <MenuItem value="ALL">ทุกสาขาวิชา</MenuItem>
                    {availableMajors.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>โรงเรียน</InputLabel>
                  <Select value={selectedSchool} label="โรงเรียน" onChange={(e) => { setSelectedSchool(e.target.value); setRepoPage(0); }}>
                    <MenuItem value="ALL">ทุกโรงเรียน</MenuItem>
                    {availableSchools.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>

          <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 2 }}>
            <Table size="small">
              <TableHead sx={{ bgcolor: '#f1f5f9' }}>
                <TableRow>
                  <TableCell align="center" sx={{ fontWeight: 'bold', width: 50 }}>ที่</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 260 }}>ชื่อเรื่องงานวิจัยในชั้นเรียน / คำสำคัญ</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 180 }}>ผู้วิจัย / สาขาวิชา</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 160 }}>สถานศึกษา / ปีการศึกษา</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', minWidth: 120 }}>วิจัยในชั้นเรียน</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', minWidth: 120 }}>บทความวิจัย</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', minWidth: 130 }}>หนังสือรับรองฯ</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', width: 100 }}>รายละเอียด</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRepo
                  .slice(repoPage * repoRowsPerPage, repoPage * repoRowsPerPage + repoRowsPerPage)
                  .map((r, idx) => (
                    <TableRow key={r.id} hover>
                      <TableCell align="center">{repoPage * repoRowsPerPage + idx + 1}</TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.dark', lineHeight: 1.35 }}>
                          {r.title_th}
                        </Typography>
                        {r.title_en && (
                          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.3 }}>
                            {r.title_en}
                          </Typography>
                        )}
                        {r.keywords && (
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.8 }}>
                            {r.keywords.split(',').map((kw: string, i: number) => kw.trim() && (
                              <Chip
                                key={i}
                                icon={<TagIcon sx={{ fontSize: '12px !important' }} />}
                                label={kw.trim()}
                                size="small"
                                sx={{ height: 20, fontSize: '0.68rem', bgcolor: '#eff6ff', color: '#1d4ed8' }}
                              />
                            ))}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {r.prefix || ''}{r.first_name} {r.last_name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                          รหัส: {r.student_code} | {r.major}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{r.school_name || '-'}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          ภาคเรียนที่ {r.semester || '-'}/{r.academic_year || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {r.report_5ch_url ? (
                          <Button
                            size="small"
                            variant="outlined"
                            color="primary"
                            startIcon={<FileIcon />}
                            href={resolveFileUrl(r.report_5ch_url)}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ fontSize: '0.72rem', textTransform: 'none' }}
                          >
                            วิจัยในชั้นเรียน
                          </Button>
                        ) : (
                          <Typography variant="caption" color="textSecondary">-</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {r.article_url ? (
                          <Button
                            size="small"
                            variant="outlined"
                            color="success"
                            startIcon={<ArticleIcon />}
                            href={resolveFileUrl(r.article_url)}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ fontSize: '0.72rem', textTransform: 'none' }}
                          >
                            บทความวิจัย
                          </Button>
                        ) : (
                          <Typography variant="caption" color="textSecondary">-</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {r.utilization_cert_url ? (
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            startIcon={<CertIcon />}
                            href={resolveFileUrl(r.utilization_cert_url)}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ fontSize: '0.72rem', textTransform: 'none' }}
                          >
                            หนังสือรับรองฯ
                          </Button>
                        ) : (
                          <Typography variant="caption" color="textSecondary">-</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => { setSelectedDetail(r); setDetailDialogOpen(true); }}
                          sx={{ fontSize: '0.72rem' }}
                        >
                          เปิดดูข้อมูล
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                {filteredRepo.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                      ไม่พบข้อมูลงานวิจัยในชั้นเรียนที่ตรงตามเงื่อนไขการสืบค้น
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[15, 25, 50, 100]}
              component="div"
              count={filteredRepo.length}
              rowsPerPage={repoRowsPerPage}
              page={repoPage}
              onPageChange={(_, p) => setRepoPage(p)}
              onRowsPerPageChange={(e) => { setRepoRowsPerPage(parseInt(e.target.value, 10)); setRepoPage(0); }}
              labelRowsPerPage="แสดงต่อหน้า:"
            />
          </TableContainer>
        </Box>
      )}

      {/* TAB (FOR STUDENT): MY CLASSROOM RESEARCH SUBMISSION & MANAGEMENT (Tab 0 for students) */}
      {isStudent && activeTab === 0 && (
        <Box>
          <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              📌 สรุปข้อมูลและการส่งผลงานวิจัยในชั้นเรียน
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              1. <strong>รายงานวิจัยในชั้นเรียน 5 บท:</strong> เชื่อมโยงกับระบบส่งภาระงานประจำวิชาโดยอัตโนมัติ (สามารถอัปโหลดหรือแก้ไขได้ที่หน้านี้หรือหน้าส่งภาระงานประจำวิชา) <br />
              2. <strong>บทความวิจัย</strong> และ <strong>หนังสือรับรองการนำไปใช้ประโยชน์:</strong> รวบรวมเข้าสู่คลังฐานข้อมูลงานวิจัยในชั้นเรียนเพื่อการเผยแพร่และสืบค้นสำหรับรุ่นถัดไป
            </Typography>
          </Alert>

          {/* Student Research Summary Table */}
          <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 2, mb: 3 }}>
            <Table size="small">
              <TableHead sx={{ bgcolor: '#f1f5f9' }}>
                <TableRow>
                  <TableCell align="center" sx={{ fontWeight: 'bold', width: 50 }}>ที่</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 180 }}>รายวิชาฝึกประสบการณ์</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 160 }}>สถานศึกษา / ภาคเรียน</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 240 }}>ชื่อเรื่องงานวิจัยในชั้นเรียน</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', minWidth: 120 }}>1. วิจัย 5 บท</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', minWidth: 120 }}>2. บทความวิจัย</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', minWidth: 130 }}>3. หนังสือรับรองฯ</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', width: 100 }}>สถานะ</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', width: 120 }}>การจัดการ</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {myPlacements.map((p, idx) => (
                  <TableRow key={p.placement_id} hover>
                    <TableCell align="center">{idx + 1}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.dark' }}>
                        {p.course_code || ''} {p.course_name || 'รายวิชาฝึกประสบการณ์'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{p.school_name || '-'}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        ภาคเรียนที่ {p.semester || '-'}/{p.academic_year || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: p.title_th ? 'text.primary' : 'text.disabled' }}>
                        {p.title_th || '(ยังไม่ได้ระบุชื่อเรื่อง)'}
                      </Typography>
                      {p.title_en && (
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                          {p.title_en}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {p.report_5ch_url ? (
                        <Tooltip title={p.report_5ch_name || 'คลิกเพื่อเปิดดูไฟล์รายงานวิจัย 5 บท'}>
                          <Chip
                            icon={<FileIcon />}
                            label="เปิดดูไฟล์"
                            size="small"
                            color="primary"
                            clickable
                            component="a"
                            href={resolveFileUrl(p.report_5ch_url)}
                            target="_blank"
                          />
                        </Tooltip>
                      ) : (
                        <Chip label="ยังไม่ส่ง" size="small" variant="outlined" sx={{ fontSize: '0.7rem', color: 'text.disabled' }} />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {p.article_url ? (
                        <Tooltip title={p.article_name || 'คลิกเพื่อเปิดดูไฟล์บทความวิจัย'}>
                          <Chip
                            icon={<ArticleIcon />}
                            label="เปิดดูไฟล์"
                            size="small"
                            color="success"
                            clickable
                            component="a"
                            href={resolveFileUrl(p.article_url)}
                            target="_blank"
                          />
                        </Tooltip>
                      ) : (
                        <Chip label="ยังไม่ส่ง" size="small" variant="outlined" sx={{ fontSize: '0.7rem', color: 'text.disabled' }} />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {p.utilization_cert_url ? (
                        <Tooltip title={p.utilization_cert_name || 'คลิกเพื่อเปิดดูไฟล์หนังสือรับรองการใช้ประโยชน์'}>
                          <Chip
                            icon={<CertIcon />}
                            label="เปิดดูไฟล์"
                            size="small"
                            color="warning"
                            clickable
                            component="a"
                            href={resolveFileUrl(p.utilization_cert_url)}
                            target="_blank"
                          />
                        </Tooltip>
                      ) : (
                        <Chip label="ยังไม่ส่ง" size="small" variant="outlined" sx={{ fontSize: '0.7rem', color: 'text.disabled' }} />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {getStatusChip(p.research_status)}
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        size="small"
                        variant="contained"
                        color={p.research_status === 'REVISION_REQUESTED' ? 'warning' : 'primary'}
                        startIcon={<EditIcon />}
                        onClick={() => handleOpenEdit(p)}
                        sx={{ fontSize: '0.72rem', textTransform: 'none', borderRadius: 1.5, fontWeight: p.research_status === 'REVISION_REQUESTED' ? 700 : 'normal' }}
                      >
                        {p.research_status === 'REVISION_REQUESTED' ? 'ส่งฉบับแก้ไข' : 'อัปโหลด/แก้ไข'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {myPlacements.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      ไม่พบข้อมูลการลงทะเบียนฝึกประสบการณ์วิชาชีพครูของท่าน
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Grid container spacing={3}>
            {myPlacements.map((p) => (
              <Grid size={{ xs: 12 }} key={p.placement_id}>
                <Card sx={{ borderRadius: 3, boxShadow: 3, borderLeft: '6px solid #1976d2' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                      <Box>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1, flexWrap: 'wrap' }}>
                          <Chip label={`${p.course_code || ''} ${p.course_name || 'รายวิชาฝึกประสบการณ์'}`} color="primary" size="small" sx={{ fontWeight: 700 }} />
                          <Chip label={`ภาคเรียนที่ ${p.semester}/${p.academic_year}`} variant="outlined" size="small" />
                          <Chip icon={<SchoolIcon />} label={`โรงเรียน${p.school_name}`} variant="outlined" size="small" />
                          {getStatusChip(p.research_status)}
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary' }}>
                          {p.title_th || '(ยังไม่ได้ระบุชื่อเรื่องงานวิจัยในชั้นเรียน)'}
                        </Typography>
                        {p.title_en && (
                          <Typography variant="body2" color="textSecondary">
                            {p.title_en}
                          </Typography>
                        )}
                      </Box>
                      <Button
                        variant="contained"
                        color={p.research_status === 'REVISION_REQUESTED' ? 'warning' : 'primary'}
                        startIcon={<EditIcon />}
                        onClick={() => handleOpenEdit(p)}
                        sx={{ borderRadius: 2, fontWeight: 700 }}
                      >
                        {p.research_status === 'REVISION_REQUESTED' ? 'อัปโหลดงานวิจัยฉบับแก้ไข' : 'อัปโหลด / แก้ไขข้อมูลงานวิจัย'}
                      </Button>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Grid container spacing={2}>
                      {/* 1. 5-Chapter Research Report */}
                      <Grid size={{ xs: 12, md: 4 }}>
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: p.report_5ch_url ? '#f0fdf4' : '#f8fafc', borderColor: p.report_5ch_url ? '#86efac' : '#e2e8f0' }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1, color: 'primary.dark' }}>
                            <FileIcon color="primary" /> 1. รายงานวิจัยในชั้นเรียน 5 บท
                          </Typography>
                          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1.5 }}>
                            (เชื่อมโยงกับภาระงานประจำวิชา)
                          </Typography>
                          {p.report_5ch_url ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                              <Chip icon={<CheckIcon />} label={p.report_5ch_name || 'ส่งไฟล์วิจัยในชั้นเรียนแล้ว'} color="success" size="small" sx={{ maxWidth: '65%' }} />
                              <Button size="small" variant="outlined" href={resolveFileUrl(p.report_5ch_url)} target="_blank" endIcon={<OpenIcon />}>
                                เปิดดู
                              </Button>
                            </Box>
                          ) : (
                            <Chip label="ยังไม่ได้อัปโหลดไฟล์วิจัยในชั้นเรียน" size="small" color="warning" variant="outlined" />
                          )}
                        </Paper>
                      </Grid>

                      {/* 2. Research Article */}
                      <Grid size={{ xs: 12, md: 4 }}>
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: p.article_url ? '#f0fdf4' : '#f8fafc', borderColor: p.article_url ? '#86efac' : '#e2e8f0' }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1, color: 'success.dark' }}>
                            <ArticleIcon color="success" /> 2. บทความวิจัย (Research Article)
                          </Typography>
                          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1.5 }}>
                            (ไม่ถือเป็นส่วนหนึ่งของคะแนนภาระงานประจำวิชา)
                          </Typography>
                          {p.article_url ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                              <Chip icon={<CheckIcon />} label={p.article_name || 'ส่งบทความวิจัยแล้ว'} color="success" size="small" sx={{ maxWidth: '65%' }} />
                              <Button size="small" variant="outlined" color="success" href={resolveFileUrl(p.article_url)} target="_blank" endIcon={<OpenIcon />}>
                                เปิดดู
                              </Button>
                            </Box>
                          ) : (
                            <Chip label="ยังไม่ได้อัปโหลดบทความวิจัย" size="small" variant="outlined" />
                          )}
                        </Paper>
                      </Grid>

                      {/* 3. Utilization Certificate */}
                      <Grid size={{ xs: 12, md: 4 }}>
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: p.utilization_cert_url ? '#f0fdf4' : '#f8fafc', borderColor: p.utilization_cert_url ? '#86efac' : '#e2e8f0' }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1, color: '#b45309' }}>
                            <CertIcon color="warning" /> 3. หนังสือรับรองการนำไปใช้ประโยชน์
                          </Typography>
                          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1.5 }}>
                            (ไม่ถือเป็นส่วนหนึ่งของคะแนนภาระงานประจำวิชา)
                          </Typography>
                          {p.utilization_cert_url ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                              <Chip icon={<CheckIcon />} label={p.utilization_cert_name || 'ส่งหนังสือรับรองแล้ว'} color="success" size="small" sx={{ maxWidth: '65%' }} />
                              <Button size="small" variant="outlined" color="warning" href={resolveFileUrl(p.utilization_cert_url)} target="_blank" endIcon={<OpenIcon />}>
                                เปิดดู
                              </Button>
                            </Box>
                          ) : (
                            <Chip label="ยังไม่ได้อัปโหลดหนังสือรับรองฯ" size="small" variant="outlined" />
                          )}
                        </Paper>
                      </Grid>
                    </Grid>

                    {(p.reviewer_notes || p.feedback_file_url) && (
                      <Alert severity={p.research_status === 'REVISION_REQUESTED' ? "warning" : "info"} sx={{ mt: 2, borderRadius: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          💬 ข้อเสนอแนะ / คำแนะนำจากอาจารย์นิเทศก์:
                        </Typography>
                        {p.reviewer_notes && (
                          <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 500 }}>
                            {p.reviewer_notes}
                          </Typography>
                        )}
                        {p.feedback_file_url && (
                          <Box sx={{ mt: 1 }}>
                            <Button
                              size="small"
                              variant="outlined"
                              color="warning"
                              href={resolveFileUrl(p.feedback_file_url)}
                              target="_blank"
                              startIcon={<DownloadIcon />}
                              sx={{ fontWeight: 700 }}
                            >
                              ดาวน์โหลดไฟล์คำแนะนำ/ตรวจแก้จากอาจารย์ ({p.feedback_file_name || 'ไฟล์คำแนะนำ'})
                            </Button>
                          </Box>
                        )}
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}

            {myPlacements.length === 0 && !loading && (
              <Grid size={{ xs: 12 }}>
                <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
                  <Typography color="textSecondary">ไม่พบข้อมูลการลงทะเบียนฝึกประสบการณ์วิชาชีพครูของท่าน</Typography>
                </Paper>
              </Grid>
            )}
          </Grid>
        </Box>
      )}

      {/* TAB 1 (FOR SUPERVISOR / STAFF / ADMIN / DEAN / RESEARCH_STAFF): MANAGE ALL STUDENTS' RESEARCH */}
      {canManageAll && activeTab === 1 && (
        <Box>
          {isSupervisor && (
            <Alert severity="info" sx={{ mb: 2.5, borderRadius: 2 }}>
              ระบบแสดงเฉพาะข้อมูลงานวิจัยในชั้นเรียนของนักศึกษาที่สังกัดสาขาวิชา <strong>{supervisorDept || 'เดียวกับอาจารย์นิเทศก์'}</strong>
            </Alert>
          )}

          <Paper sx={{ p: 2.5, mb: 3, borderRadius: 2 }}>
            <Grid container spacing={2} sx={{ alignItems: 'center' }}>
              <Grid size={{ xs: 12, md: 3 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="ค้นหารหัสนักศึกษา, ชื่อ-สกุล, ชื่อเรื่องวิจัย..."
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setManagePage(0); }}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon color="primary" />
                        </InputAdornment>
                      )
                    }
                  }}
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>ปีการศึกษา</InputLabel>
                  <Select value={selectedYear} label="ปีการศึกษา" onChange={(e) => { setSelectedYear(e.target.value); setManagePage(0); }}>
                    <MenuItem value="ALL">ทุกปีการศึกษา</MenuItem>
                    {availableYears.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 6, sm: 1.5 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>ภาคเรียน</InputLabel>
                  <Select value={selectedSemester} label="ภาคเรียน" onChange={(e) => { setSelectedSemester(e.target.value); setManagePage(0); }}>
                    <MenuItem value="ALL">ทั้งหมด</MenuItem>
                    <MenuItem value="1">1</MenuItem>
                    <MenuItem value="2">2</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>สาขาวิชา</InputLabel>
                  <Select value={selectedMajor} label="สาขาวิชา" onChange={(e) => { setSelectedMajor(e.target.value); setManagePage(0); }}>
                    <MenuItem value="ALL">ทุกสาขาวิชา</MenuItem>
                    {availableMajors.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 1.5 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>โรงเรียน</InputLabel>
                  <Select value={selectedSchool} label="โรงเรียน" onChange={(e) => { setSelectedSchool(e.target.value); setManagePage(0); }}>
                    <MenuItem value="ALL">ทุกโรงเรียน</MenuItem>
                    {availableSchools.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>สถานะผลงาน</InputLabel>
                  <Select value={selectedStatus} label="สถานะผลงาน" onChange={(e) => { setSelectedStatus(e.target.value); setManagePage(0); }}>
                    <MenuItem value="ALL">ทุกสถานะ</MenuItem>
                    <MenuItem value="HAS_5CH">ส่งวิจัยในชั้นเรียนแล้ว</MenuItem>
                    <MenuItem value="HAS_ARTICLE">ส่งบทความวิจัยแล้ว</MenuItem>
                    <MenuItem value="HAS_CERT">ส่งหนังสือรับรองฯ แล้ว</MenuItem>
                    <MenuItem value="PUBLISHED">อนุมัติและเผยแพร่ในฐานข้อมูลแล้ว</MenuItem>
                    <MenuItem value="REVISION_REQUESTED">ขอให้นักศึกษาปรับปรุงแก้ไข</MenuItem>
                    <MenuItem value="NOT_SUBMITTED">ยังไม่ส่งผลงาน</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>

          <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 2 }}>
            <Table size="small">
              <TableHead sx={{ bgcolor: '#f1f5f9' }}>
                <TableRow>
                  <TableCell align="center" sx={{ fontWeight: 'bold', width: 45 }}>ที่</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 190 }}>นักศึกษา / สาขาวิชา</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 160 }}>โรงเรียน / รายวิชา</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 220 }}>ชื่อเรื่องงานวิจัยในชั้นเรียน</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', minWidth: 105 }}>วิจัยในชั้นเรียน</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', minWidth: 105 }}>บทความวิจัย</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', minWidth: 115 }}>หนังสือรับรองฯ</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', minWidth: 130 }}>การประเมินภาระงาน</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', minWidth: 120 }}>สถานะงานวิจัย</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', minWidth: 130 }}>การจัดการ</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredManage
                  .slice(managePage * manageRowsPerPage, managePage * manageRowsPerPage + manageRowsPerPage)
                  .map((r, idx) => (
                    <TableRow key={r.placement_id} hover>
                      <TableCell align="center">{managePage * manageRowsPerPage + idx + 1}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {r.prefix || ''}{r.first_name} {r.last_name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                          {r.student_code} | {r.major}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{r.school_name || '-'}</Typography>
                        <Typography variant="caption" color="primary" sx={{ fontWeight: 600 }}>
                          {r.course_code} ({r.semester}/{r.academic_year})
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {r.title_th ? (
                          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                            {r.title_th}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                            ยังไม่ได้ระบุชื่อเรื่อง
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {r.report_5ch_url ? (
                          <Tooltip title="คลิกเพื่อเปิดไฟล์รายงานวิจัยในชั้นเรียน">
                            <Chip
                              icon={<FileIcon />}
                              label="เปิดดูไฟล์"
                              size="small"
                              color="primary"
                              clickable
                              component="a"
                              href={resolveFileUrl(r.report_5ch_url)}
                              target="_blank"
                            />
                          </Tooltip>
                        ) : (
                          <Chip label="ยังไม่ส่ง" size="small" variant="outlined" sx={{ fontSize: '0.7rem', color: 'text.disabled' }} />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {r.article_url ? (
                          <Tooltip title="คลิกเพื่อเปิดไฟล์บทความวิจัย">
                            <Chip
                              icon={<ArticleIcon />}
                              label="เปิดดูไฟล์"
                              size="small"
                              color="success"
                              clickable
                              component="a"
                              href={resolveFileUrl(r.article_url)}
                              target="_blank"
                            />
                          </Tooltip>
                        ) : (
                          <Chip label="ยังไม่ส่ง" size="small" variant="outlined" sx={{ fontSize: '0.7rem', color: 'text.disabled' }} />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {r.utilization_cert_url ? (
                          <Tooltip title="คลิกเพื่อเปิดไฟล์หนังสือรับรองการนำไปใช้ประโยชน์">
                            <Chip
                              icon={<CertIcon />}
                              label="เปิดดูไฟล์"
                              size="small"
                              color="warning"
                              clickable
                              component="a"
                              href={resolveFileUrl(r.utilization_cert_url)}
                              target="_blank"
                            />
                          </Tooltip>
                        ) : (
                          <Chip label="ยังไม่ส่ง" size="small" variant="outlined" sx={{ fontSize: '0.7rem', color: 'text.disabled' }} />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {r.workload_evaluation_id ? (
                          isStudent ? (
                            <Tooltip title="ได้รับการบันทึกคะแนนประเมินภาระงานเรียบร้อยแล้ว (สถานะเผยแพร่ในฐานข้อมูลสมบูรณ์)">
                              <Chip
                                icon={<CheckIcon sx={{ fontSize: '0.9rem !important' }} />}
                                label="ประเมินภาระงานแล้ว"
                                size="small"
                                color="success"
                                sx={{ fontWeight: 700, fontSize: '0.72rem' }}
                              />
                            </Tooltip>
                          ) : (
                            <Tooltip title={`ประเมินแล้ว: ${r.workload_total_score} คะแนน โดย ${r.workload_evaluator_name || 'อาจารย์นิเทศก์'}`}>
                              <Chip
                                icon={<CheckIcon sx={{ fontSize: '0.9rem !important' }} />}
                                label={`${r.workload_total_score} คะแนน`}
                                size="small"
                                color="success"
                                sx={{ fontWeight: 700, fontSize: '0.72rem' }}
                              />
                            </Tooltip>
                          )
                        ) : (
                          <Tooltip title="อาจารย์นิเทศก์ยังไม่ได้บันทึกคะแนนประเมินภาระงาน">
                            <Chip
                              icon={<ClockIcon sx={{ fontSize: '0.9rem !important' }} />}
                              label="รอประเมินภาระงาน"
                              size="small"
                              variant="outlined"
                              color="warning"
                              sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                            />
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {getStatusChip(r.research_status)}
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', alignItems: 'center' }}>
                          <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            startIcon={<EditIcon />}
                            onClick={() => handleOpenEdit(r)}
                            sx={{ fontSize: '0.72rem', textTransform: 'none', borderRadius: 1.5 }}
                          >
                            จัดการข้อมูล
                          </Button>
                          {(r.report_5ch_url || r.article_url || r.utilization_cert_url || r.workload_5ch_url || r.research_id) && (
                            <Tooltip title="รีเซตข้อมูลงานวิจัยและลบไฟล์ทั้งหมดเพื่อให้นักศึกษาส่งใหม่">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleResetResearch(r)}
                                sx={{ bgcolor: 'rgba(239, 68, 68, 0.08)', '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.18)' } }}
                              >
                                <RestartAltIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                {filteredManage.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                      ไม่พบข้อมูลนักศึกษาตามเงื่อนไขที่เลือก
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[25, 50, 100]}
              component="div"
              count={filteredManage.length}
              rowsPerPage={manageRowsPerPage}
              page={managePage}
              onPageChange={(_, p) => setManagePage(p)}
              onRowsPerPageChange={(e) => { setManageRowsPerPage(parseInt(e.target.value, 10)); setManagePage(0); }}
              labelRowsPerPage="แสดงต่อหน้า:"
            />
          </TableContainer>
        </Box>
      )}

      {/* DETAIL MODAL (VIEW ABSTRACT & RESEARCH DETAILS) */}
      <Dialog open={detailDialogOpen} onClose={() => setDetailDialogOpen(false)} fullWidth maxWidth="md" disableRestoreFocus>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'primary.main', color: '#ffffff' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ResearchIcon sx={{ color: '#ffffff' }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#ffffff' }}>รายละเอียดผลงานวิจัยในชั้นเรียน</Typography>
          </Box>
          <IconButton onClick={() => setDetailDialogOpen(false)} sx={{ color: '#ffffff' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          {selectedDetail && (
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: 'primary.dark', mb: 0.5 }}>
                {selectedDetail.title_th}
              </Typography>
              {selectedDetail.title_en && (
                <Typography variant="subtitle1" color="textSecondary" sx={{ mb: 2 }}>
                  {selectedDetail.title_en}
                </Typography>
              )}

              <Grid container spacing={2} sx={{ mb: 2.5, p: 2, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2"><strong>ผู้วิจัย:</strong> {selectedDetail.prefix || ''}{selectedDetail.first_name} {selectedDetail.last_name} ({selectedDetail.student_code})</Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}><strong>สาขาวิชา:</strong> {selectedDetail.major}</Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}><strong>อาจารย์ที่ปรึกษา/นิเทศก์:</strong> {selectedDetail.advisor_name || '-'}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2"><strong>สถานศึกษา:</strong> {selectedDetail.school_name || '-'}</Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}><strong>ระดับชั้น/กลุ่มเป้าหมาย:</strong> {selectedDetail.target_grade_level || '-'} ({selectedDetail.subject_name || '-'})</Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}><strong>ปีการศึกษา:</strong> ภาคเรียนที่ {selectedDetail.semester || '-'}/{selectedDetail.academic_year || '-'}</Typography>
                </Grid>
              </Grid>

              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>บทคัดย่อ (Abstract):</Typography>
              <Paper variant="outlined" sx={{ p: 2, mb: 2.5, bgcolor: '#fcfcfd', borderRadius: 2, whiteSpace: 'pre-wrap' }}>
                <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                  {selectedDetail.abstract || 'ไม่ได้ระบุบทคัดย่อ'}
                </Typography>
              </Paper>

              {selectedDetail.keywords && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.8 }}>คำสำคัญ (Keywords):</Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {selectedDetail.keywords.split(',').map((kw: string, i: number) => kw.trim() && (
                      <Chip key={i} label={kw.trim()} color="primary" variant="outlined" size="small" />
                    ))}
                  </Box>
                </Box>
              )}

              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>ไฟล์เอกสารผลงานวิจัย:</Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Button
                    fullWidth
                    variant={selectedDetail.report_5ch_url ? 'contained' : 'outlined'}
                    color="primary"
                    disabled={!selectedDetail.report_5ch_url}
                    startIcon={<FileIcon />}
                    href={resolveFileUrl(selectedDetail.report_5ch_url)}
                    target="_blank"
                    sx={{ py: 1.2, borderRadius: 2 }}
                  >
                    {selectedDetail.report_5ch_url ? 'ดาวน์โหลดวิจัยในชั้นเรียน' : 'ไม่มีไฟล์วิจัยในชั้นเรียน'}
                  </Button>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Button
                    fullWidth
                    variant={selectedDetail.article_url ? 'contained' : 'outlined'}
                    color="success"
                    disabled={!selectedDetail.article_url}
                    startIcon={<ArticleIcon />}
                    href={resolveFileUrl(selectedDetail.article_url)}
                    target="_blank"
                    sx={{ py: 1.2, borderRadius: 2 }}
                  >
                    {selectedDetail.article_url ? 'ดาวน์โหลดบทความวิจัย' : 'ไม่มีไฟล์บทความวิจัย'}
                  </Button>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Button
                    fullWidth
                    variant={selectedDetail.utilization_cert_url ? 'contained' : 'outlined'}
                    color="warning"
                    disabled={!selectedDetail.utilization_cert_url}
                    startIcon={<CertIcon />}
                    href={resolveFileUrl(selectedDetail.utilization_cert_url)}
                    target="_blank"
                    sx={{ py: 1.2, borderRadius: 2 }}
                  >
                    {selectedDetail.utilization_cert_url ? 'หนังสือรับรองการใช้ประโยชน์' : 'ไม่มีหนังสือรับรองฯ'}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDetailDialogOpen(false)}>ปิดหน้าต่าง</Button>
        </DialogActions>
      </Dialog>

      {/* EDIT / UPLOAD MODAL */}
      <Dialog open={editDialogOpen} onClose={() => !saving && setEditDialogOpen(false)} fullWidth maxWidth="md" disableRestoreFocus>
        <DialogTitle sx={{ bgcolor: 'primary.main', color: '#ffffff', fontWeight: 700 }}>
          จัดการข้อมูลงานวิจัยในชั้นเรียน บทความวิจัย และหนังสือรับรองการนำไปใช้ประโยชน์
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          {editingRecord && (
            <Box>
              <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                <strong>ผู้วิจัย:</strong> {editingRecord.prefix || ''}{editingRecord.first_name} {editingRecord.last_name} ({editingRecord.student_code}) | <strong>สาขาวิชา:</strong> {editingRecord.major} | <strong>สถานศึกษา:</strong> {editingRecord.school_name}
              </Alert>

              {!editingRecord.workload_evaluation_id ? (
                <Alert
                  severity="warning"
                  icon={<WarningIcon />}
                  action={
                    ['SUPERVISOR', 'ADMIN', 'STAFF'].includes(user.role) ? (
                      <Button
                        size="small"
                        variant="outlined"
                        color="inherit"
                        onClick={() => {
                          setEditDialogOpen(false);
                          navigate('/dashboard/workload-evaluation');
                        }}
                        sx={{ fontWeight: 700, fontSize: '0.75rem', borderColor: 'currentColor', whiteSpace: 'nowrap' }}
                      >
                        ไปที่หน้ากรอกคะแนนภาระงาน
                      </Button>
                    ) : undefined
                  }
                  sx={{ mb: 2.5, borderRadius: 2 }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    แจ้งเตือน: นักศึกษารายนี้ยังไม่ได้รับการบันทึกคะแนนประเมินภาระงานจากอาจารย์นิเทศก์
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.primary' }}>
                    ระบบจะทำการอนุมัติและเผยแพร่งานวิจัยในชั้นเรียน (PUBLISHED) อัตโนมัติเมื่ออาจารย์นิเทศก์ได้ประเมินและบันทึกคะแนนภาระงานแล้ว ท่านยังคงสามารถแก้ไขข้อมูลชื่อเรื่อง บทคัดย่อ หรือแนบไฟล์บทความวิจัย/หนังสือรับรองฯ ได้ตามปกติ แต่สถานะงานวิจัยจะคงไว้ที่ "รอการประเมินภาระงาน" จนกว่าอาจารย์นิเทศก์จะบันทึกคะแนน
                  </Typography>
                </Alert>
              ) : (
                <Alert severity="success" icon={<CheckIcon />} sx={{ mb: 2.5, borderRadius: 2 }}>
                  {isStudent ? (
                    <Tooltip title="บันทึกคะแนนภาระงานแล้ว (สถานะเผยแพร่ในฐานข้อมูลสมบูรณ์)">
                      <span>
                        <strong>บันทึกคะแนนภาระงานแล้ว:</strong> ได้รับการประเมินเรียบร้อยแล้ว (สถานะเผยแพร่ในฐานข้อมูลสมบูรณ์)
                      </span>
                    </Tooltip>
                  ) : (
                    <Tooltip title={`บันทึกคะแนนภาระงานแล้ว: ได้รับคะแนน ${editingRecord.workload_total_score} คะแนน โดย ${editingRecord.workload_evaluator_name || 'อาจารย์นิเทศก์'} (สถานะเผยแพร่ในฐานข้อมูลสมบูรณ์)`}>
                      <span>
                        <strong>บันทึกคะแนนภาระงานแล้ว:</strong> ได้รับคะแนน {editingRecord.workload_total_score} คะแนน โดย {editingRecord.workload_evaluator_name || 'อาจารย์นิเทศก์'} (สถานะเผยแพร่ในฐานข้อมูลสมบูรณ์)
                      </span>
                    </Tooltip>
                  )}
                </Alert>
              )}

              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    required
                    label="ชื่อเรื่องงานวิจัยในชั้นเรียน (ภาษาไทย) *"
                    placeholder="เช่น การพัฒนาผลสัมฤทธิ์ทางการเรียนวิชา... โดยใช้ชุดกิจกรรมการเรียนรู้..."
                    value={formState.title_th}
                    onChange={(e) => setFormState({ ...formState, title_th: e.target.value })}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="ชื่อเรื่องงานวิจัยในชั้นเรียน (ภาษาอังกฤษ)"
                    placeholder="Development of Learning Achievement in..."
                    value={formState.title_en}
                    onChange={(e) => setFormState({ ...formState, title_en: e.target.value })}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="รายวิชาที่ทำวิจัย"
                    placeholder="เช่น คณิตศาสตร์พื้นฐาน, ภาษาไทย"
                    value={formState.subject_name}
                    onChange={(e) => setFormState({ ...formState, subject_name: e.target.value })}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="ระดับชั้น/กลุ่มเป้าหมาย"
                    placeholder="เช่น ชั้นประถมศึกษาปีที่ 5, ชั้นมัธยมศึกษาปีที่ 2/1"
                    value={formState.target_grade_level}
                    onChange={(e) => setFormState({ ...formState, target_grade_level: e.target.value })}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="อาจารย์นิเทศก์ / อาจารย์ที่ปรึกษางานวิจัย"
                    value={formState.advisor_name}
                    onChange={(e) => setFormState({ ...formState, advisor_name: e.target.value })}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="หน่วยงานที่นำผลการวิจัยไปใช้ประโยชน์"
                    placeholder="เช่น โรงเรียน..."
                    value={formState.utilization_org}
                    onChange={(e) => setFormState({ ...formState, utilization_org: e.target.value })}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="คำสำคัญ (Keywords) คั่นด้วยเครื่องหมายจุลภาค (,)"
                    placeholder="เช่น ผลสัมฤทธิ์ทางการเรียน, ชุดกิจกรรมการเรียนรู้, การจัดการเรียนรู้แบบ Active Learning"
                    value={formState.keywords}
                    onChange={(e) => setFormState({ ...formState, keywords: e.target.value })}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="บทคัดย่อ (Abstract)"
                    placeholder="สรุปวัตถุประสงค์ กลุ่มเป้าหมาย เครื่องมือที่ใช้ และการวิเคราะห์ผลการวิจัย..."
                    value={formState.abstract}
                    onChange={(e) => setFormState({ ...formState, abstract: e.target.value })}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Divider sx={{ my: 1 }}>
                    <Chip label="ส่วนของการอัปโหลดไฟล์เอกสารทั้ง 3 ส่วน" color="primary" size="small" />
                  </Divider>
                </Grid>

                {/* File 1: 5-Chapter Research Report */}
                <Grid size={{ xs: 12, md: 4 }}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.main', mb: 0.5 }}>
                      1. วิจัยในชั้นเรียน 5 บท
                    </Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1.5 }}>
                      เชื่อมโยงกับภาระงานประจำวิชา (PDF/Word)
                    </Typography>
                    {editingRecord.report_5ch_url && (
                      <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Button size="small" href={resolveFileUrl(editingRecord.report_5ch_url)} target="_blank" startIcon={<OpenIcon />}>
                          ดูไฟล์ปัจจุบัน
                        </Button>
                        {editingRecord.research_id && (
                          <IconButton size="small" color="error" onClick={() => handleDeleteFile(editingRecord.research_id, 'report_5ch', 'รายงานวิจัยในชั้นเรียน')}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    )}
                    <Button variant="outlined" component="label" fullWidth startIcon={<UploadIcon />} size="small">
                      {report5chFile ? report5chFile.name : 'เลือกไฟล์วิจัยในชั้นเรียน'}
                      <input type="file" hidden accept=".pdf,.doc,.docx" onChange={(e) => setReport5chFile(e.target.files?.[0] || null)} />
                    </Button>
                  </Paper>
                </Grid>

                {/* File 2: Research Article */}
                <Grid size={{ xs: 12, md: 4 }}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'success.dark', mb: 0.5 }}>
                      2. บทความวิจัย
                    </Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1.5 }}>
                      ไม่คิดเป็นคะแนนภาระงานประจำวิชา (PDF/Word)
                    </Typography>
                    {editingRecord.article_url && (
                      <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Button size="small" color="success" href={resolveFileUrl(editingRecord.article_url)} target="_blank" startIcon={<OpenIcon />}>
                          ดูไฟล์ปัจจุบัน
                        </Button>
                        {editingRecord.research_id && (
                          <IconButton size="small" color="error" onClick={() => handleDeleteFile(editingRecord.research_id, 'article', 'บทความวิจัย')}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    )}
                    <Button variant="outlined" color="success" component="label" fullWidth startIcon={<UploadIcon />} size="small">
                      {articleFile ? articleFile.name : 'เลือกไฟล์บทความวิจัย'}
                      <input type="file" hidden accept=".pdf,.doc,.docx" onChange={(e) => setArticleFile(e.target.files?.[0] || null)} />
                    </Button>
                  </Paper>
                </Grid>

                {/* File 3: Utilization Certificate */}
                <Grid size={{ xs: 12, md: 4 }}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#b45309', mb: 0.5 }}>
                      3. หนังสือรับรองการใช้ประโยชน์
                    </Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1.5 }}>
                      ไม่คิดเป็นคะแนนภาระงานประจำวิชา (PDF/รูปภาพ)
                    </Typography>
                    {editingRecord.utilization_cert_url && (
                      <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Button size="small" color="warning" href={resolveFileUrl(editingRecord.utilization_cert_url)} target="_blank" startIcon={<OpenIcon />}>
                          ดูไฟล์ปัจจุบัน
                        </Button>
                        {editingRecord.research_id && (
                          <IconButton size="small" color="error" onClick={() => handleDeleteFile(editingRecord.research_id, 'utilization_cert', 'หนังสือรับรองการนำไปใช้ประโยชน์')}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    )}
                    <Button variant="outlined" color="warning" component="label" fullWidth startIcon={<UploadIcon />} size="small">
                      {certFile ? certFile.name : 'เลือกไฟล์หนังสือรับรองฯ'}
                      <input type="file" hidden accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={(e) => setCertFile(e.target.files?.[0] || null)} />
                    </Button>
                  </Paper>
                </Grid>

                {/* Reviewer Section (Only for Staff / Supervisor / Co-Teacher / Admin / Research Staff) */}
                {canManageAll && (
                  <>
                    <Grid size={{ xs: 12 }}>
                      <Divider sx={{ my: 1 }}>
                        <Chip label="ส่วนสำหรับอาจารย์นิเทศก์ / เจ้าหน้าที่ฝ่ายวิจัย / ผู้ดูแลระบบ" color="secondary" size="small" />
                      </Divider>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel>สถานะการตรวจสอบและเผยแพร่</InputLabel>
                        <Select
                          value={formState.status === 'VERIFIED' ? 'PUBLISHED' : formState.status}
                          label="สถานะการตรวจสอบและเผยแพร่"
                          onChange={(e) => setFormState({ ...formState, status: e.target.value })}
                        >
                          <MenuItem value="SUBMITTED">
                            {editingRecord.workload_evaluation_id
                              ? 'ส่งผลงานแล้ว (รอตรวจสอบ)'
                              : 'ส่งผลงานแล้ว (รออาจารย์นิเทศก์ประเมินภาระงาน)'}
                          </MenuItem>
                          <MenuItem
                            value="PUBLISHED"
                            disabled={!editingRecord.workload_evaluation_id}
                          >
                            อนุมัติและเผยแพร่ในฐานข้อมูล {!editingRecord.workload_evaluation_id && '(ต้องให้อาจารย์นิเทศก์บันทึกคะแนนภาระงานก่อน)'}
                          </MenuItem>
                          <MenuItem value="REVISION_REQUESTED">ขอให้นักศึกษาปรับปรุงแก้ไข</MenuItem>
                        </Select>
                        {!editingRecord.workload_evaluation_id && (
                          <FormHelperText sx={{ color: 'warning.dark', fontWeight: 600 }}>
                            * สถานะจะเปลี่ยนเป็น "อนุมัติและเผยแพร่ในฐานข้อมูล" อัตโนมัติเมื่ออาจารย์นิเทศก์บันทึกคะแนนภาระงาน
                          </FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                  </>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {canManageAll && (editingRecord?.research_id || editingRecord?.report_5ch_url || editingRecord?.workload_5ch_url || editingRecord?.article_url || editingRecord?.utilization_cert_url) ? (
            <Button
              color="error"
              variant="outlined"
              startIcon={<RestartAltIcon />}
              onClick={() => handleResetResearch(editingRecord)}
              disabled={saving}
              sx={{ fontWeight: 600, borderRadius: 2 }}
            >
              รีเซตข้อมูลและลบไฟล์ทั้งหมด
            </Button>
          ) : (
            <Box />
          )}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={() => setEditDialogOpen(false)} disabled={saving}>ยกเลิก</Button>
            <Button variant="contained" color="primary" onClick={handleSaveResearch} disabled={saving} sx={{ borderRadius: 2, fontWeight: 700 }}>
              {saving ? 'กำลังบันทึก...' : 'บันทึกข้อมูลงานวิจัย'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ClassroomResearchDatabase;
